import { useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { IconX } from "@/components/icons";
import { TextInput } from "@/components/admin/AdminFields";
import {
  mergeChoiceLines,
  parseChoiceLines,
} from "@/lib/formBuilder/choiceLines";

export function StringChipsEditor({
  values,
  onChange,
  placeholder = "Имя и Enter",
  disabled,
  selectedValues,
  onToggleSelected,
  selectAriaLabel = "Отметить как верный",
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  selectedValues?: string[];
  onToggleSelected?: (value: string) => void;
  selectAriaLabel?: string;
}) {
  const [draft, setDraft] = useState("");

  const addLines = (text: string) => {
    const next = mergeChoiceLines(values, text);
    if (next.length !== values.length) onChange(next);
    setDraft("");
  };

  const addDraft = () => {
    if (!draft.trim()) return;
    addLines(draft);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    addDraft();
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    const lines = parseChoiceLines(text);
    if (lines.length < 2) return;
    event.preventDefault();
    addLines([draft, text].filter(Boolean).join("\n"));
  };

  return (
    <div className="space-y-1.5">
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {values.map((value) => {
            const selected = selectedValues?.includes(value);
            return (
              <span
                key={value}
                className={[
                  "inline-flex max-w-full items-center gap-1 rounded-full py-0.5 pl-2 pr-1 font-onest text-[11px]",
                  selected
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-100 text-slate-700",
                ].join(" ")}
              >
                {onToggleSelected ? (
                  <button
                    type="button"
                    aria-pressed={selected}
                    aria-label={`${selectAriaLabel}: ${value}`}
                    onClick={() => onToggleSelected(value)}
                    className="truncate text-left"
                  >
                    {value}
                  </button>
                ) : (
                  <span className="truncate">{value}</span>
                )}
                {disabled ? null : (
                  <button
                    type="button"
                    aria-label={`Убрать «${value}»`}
                    onClick={() =>
                      onChange(values.filter((item) => item !== value))
                    }
                    className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <IconX size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      ) : null}
      {disabled ? null : (
        <TextInput
          size="compact"
          value={draft}
          placeholder={placeholder}
          onChange={(value) => {
            if (parseChoiceLines(value).length > 1) {
              addLines(value);
              return;
            }
            setDraft(value);
          }}
          onBlur={addDraft}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      )}
    </div>
  );
}
